import React from 'react';

import type { Conversation } from '../Conversation';

// The live steps panel pins to a single step group, identified by its
// anchor id (derived from the message that opened the group — the same key
// `groupSteps` uses). The panel re-derives that group live from the
// conversation, so only the anchor id needs to live here.
export interface StepsPanelState {
    open: boolean;
    anchorId: string | null;
    /** A subagent the panel is showing in place of the pinned group; back returns to the group. */
    watchedConversation: Conversation | null;
    /** Whether a panel is mounted to open, so a surface without one can hide its watch buttons. */
    isAvailable: boolean;
}

let state: StepsPanelState = {
    open: false,
    anchorId: null,
    watchedConversation: null,
    isAvailable: false,
};
let mountedPanels = 0;
const listeners = new Set<() => void>();

function setState(next: Partial<StepsPanelState>): void {
    state = { ...state, ...next };
    for (const listener of listeners) {
        listener();
    }
}

/**
 * Opens the steps panel pinned to the given step group. Called from the
 * live shimmer while a turn is executing (a finished turn's "Steps" toggle
 * expands inline instead). Buried deep in the renderer, the shimmer reaches the
 * panel through this module singleton rather than prop-drilling a callback up to
 * ChatTab.
 */
export function openStepsPanel(anchorId: string): void {
    setState({ open: true, anchorId, watchedConversation: null });
}

/** Shows a subagent's steps, keeping any pinned group to go back to. */
export function watchConversationInStepsPanel(conversation: Conversation): void {
    setState({ open: true, watchedConversation: conversation });
}

/** Leaves a watched subagent for the pinned group, or closes when there is none. */
export function showPinnedStepGroup(): void {
    if (state.anchorId === null) {
        closeStepsPanel();
        return;
    }
    setState({ watchedConversation: null });
}

export function closeStepsPanel(): void {
    if (!state.open) {
        return;
    }
    setState({ open: false, anchorId: null, watchedConversation: null });
}

/** Called by a mounted panel; returns the cleanup for its unmount. */
export function registerStepsPanel(): () => void {
    mountedPanels += 1;
    setState({ isAvailable: true });
    return () => {
        mountedPanels -= 1;
        if (mountedPanels === 0) {
            setState({
                isAvailable: false,
                open: false,
                anchorId: null,
                watchedConversation: null,
            });
        }
    };
}

function getState(): StepsPanelState {
    return state;
}

export function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/** React binding so the panel re-renders when it opens, closes, or re-pins. */
export function useStepsPanel(): StepsPanelState {
    return React.useSyncExternalStore(subscribe, getState, getState);
}
